// packages/web-ui/src/components/capability-pills/HealthPill.tsx
//
// Story W3-S6 AC #3 — `<HealthPill>` primitive. A small pill with a
// state-coloured dot and a label, optionally clickable to open the relevant
// settings category. The same primitive is reused by the chat-session-header
// capability chip row (UX §6.1.3) once W2-S5/S6 lands the wiring.
//
// Polymorphic element type:
//   - `state === 'missing'` + `onConfigure` provided → `<button>` with chevron
//     (announces "click to configure" for screen readers).
//   - otherwise → `<span>` with `role="status"` (passive status indicator).
//
// `prefers-reduced-motion`:
//   - `motion-reduce:` modifiers strip the colour transition + the loading
//     pulse animation per UX §4.5 + Arch §8.8.
//
// Tooltip:
//   - v1 uses the `title` HTML attribute. Radix `<Tooltip>` migration is
//     deferred to W6-S2 (W3-S6 AC #3 deliverable 3).

import type { CSSProperties, JSX } from 'react';
import { ChevronRight } from 'lucide-react';

export type HealthPillState = 'ok' | 'missing' | 'warn' | 'error' | 'loading';

export interface HealthPillProps {
  /** Short user-visible label, e.g. `"Git"`, `"KB"`, `"DA"`. */
  label: string;
  /** Visual + a11y state. */
  state: HealthPillState;
  /** Optional `title=` tooltip. */
  tooltip?: string;
  /**
   * Click handler — only honoured when `state === 'missing'`. Causes the
   * pill to render as a `<button>` with a chevron. When omitted (or for
   * any other state) the pill renders as an inert `<span>`.
   */
  onConfigure?: () => void;
  /** Optional `data-testid` override. */
  testId?: string;
}

// Audi pass: pills remain pill-shaped (the only `rounded-full` surface
// kept in the brand), but typography goes uppercase-tracked for the
// "engineering chip" feel matching audi.com's data badges.
const BASE_CLASS =
  'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-[0.06em] border ' +
  'transition-colors motion-reduce:transition-none';

const STATE_TO_CLASS: Record<HealthPillState, string> = {
  ok: 'bg-cream-50 border-accent-green text-fg',
  missing: 'bg-cream-100 border-accent-red text-fg',
  warn: 'bg-cream-100 border-accent-amber text-fg',
  error: 'bg-cream-100 border-accent-red text-accent-red',
  loading:
    'bg-cream-200 border-taupe-300 text-taupe-500 animate-pulse motion-reduce:animate-none',
};

const STATE_TO_DOT: Record<HealthPillState, string> = {
  ok: 'h-1.5 w-1.5 rounded-full bg-accent-green',
  missing: 'h-1.5 w-1.5 rounded-full bg-accent-red',
  warn: 'h-1.5 w-1.5 rounded-full bg-accent-amber',
  error: 'h-1.5 w-1.5 rounded-full bg-accent-red',
  loading: 'h-1.5 w-1.5 rounded-full bg-taupe-300',
};

/**
 * Renders the capability pill. Decoupled from any specific data source —
 * `<CapabilityPillRow>` is responsible for projecting capability flags into
 * `state` values; this component is purely presentational.
 */
export function HealthPill({
  label,
  state,
  tooltip,
  onConfigure,
  testId,
}: HealthPillProps): JSX.Element {
  const dotStyle: CSSProperties = { display: 'inline-block' };
  const dataTestId = testId ?? `health-pill-${label.toLowerCase()}`;

  if (state === 'missing' && onConfigure !== undefined) {
    return (
      <button
        type="button"
        data-testid={dataTestId}
        data-state={state}
        title={tooltip}
        aria-label={`${label} capability: missing. Click to configure.`}
        onClick={onConfigure}
        className={
          `${BASE_CLASS} ${STATE_TO_CLASS[state]} ` +
          `hover:bg-cream-100 hover:border-audi-red hover:text-audi-red cursor-pointer ` +
          `focus-visible:outline-2 focus-visible:outline-taupe-900 focus-visible:outline-offset-2 ` +
          `outline-none`
        }
      >
        <span className={STATE_TO_DOT[state]} style={dotStyle} aria-hidden="true" />
        <span>{label}</span>
        <ChevronRight size={12} aria-hidden={true} />
      </button>
    );
  }

  return (
    <span
      data-testid={dataTestId}
      data-state={state}
      title={tooltip}
      aria-label={`${label} capability: ${state}`}
      role="status"
      className={`${BASE_CLASS} ${STATE_TO_CLASS[state]}`}
    >
      <span className={STATE_TO_DOT[state]} style={dotStyle} aria-hidden="true" />
      <span>{label}</span>
    </span>
  );
}
